package com.sddamico.pages

import androidx.compose.runtime.*
import com.sddamico.HeadingFontFamily
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.apiGet
import com.sddamico.api.apiJson
import com.sddamico.api.apiPost
import com.sddamico.components.layouts.PageLayoutData
import com.sddamico.components.widgets.ConfirmableButton
import com.sddamico.components.widgets.EmptyState
import com.sddamico.components.widgets.ErrorCard
import com.sddamico.components.widgets.PageLoading
import com.sddamico.components.widgets.MoveCard
import com.sddamico.components.widgets.PendingClaimCard
import com.sddamico.components.widgets.PickTradeCard
import com.sddamico.models.*
import com.sddamico.state.LocalAuthState
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initRosterMoves(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Manage Rosters"))
}

@Page("manage-rosters")
@Layout(".components.layouts.PageLayout")
@Composable
fun RosterMovesPage() {
    val authState = LocalAuthState.current
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    // Auth guard — use realUserRole during impersonation so admin retains access
    val effectiveRole = authState.user?.realUserRole ?: authState.user?.role
    LaunchedEffect(authState.loading, authState.user) {
        if (!authState.loading && (authState.user == null || (effectiveRole != "LEAGUE_MANAGER" && effectiveRole != "LEAGUE_ADMIN"))) {
            ctx.router.tryRoutingTo("/")
        }
    }

    if (authState.loading || authState.user == null || (effectiveRole != "LEAGUE_MANAGER" && effectiveRole != "LEAGUE_ADMIN")) {
        PageLoading()
        return
    }

    // Pending claims state
    var pendingClaims by remember { mutableStateOf<List<PlayerClaimResponse>>(emptyList()) }
    var claimsRefreshKey by remember { mutableStateOf(0) }

    LaunchedEffect(claimsRefreshKey) {
        try {
            val resp = apiGet("/api/claims/pending", PlayerClaimListResponse.serializer())
            pendingClaims = resp.claims
        } catch (_: Exception) {}
    }

    // State
    var moveType by remember { mutableStateOf("ADD_DROP") }
    var clubs by remember { mutableStateOf<List<ClubResponse>>(emptyList()) }
    var selectedClubId by remember { mutableStateOf("") }
    var roster by remember { mutableStateOf<List<RosterPlayerResponse>>(emptyList()) }
    var selectedSlot by remember { mutableStateOf("") }
    var playerMode by remember { mutableStateOf("existing") } // "existing" or "new"
    var allPlayers by remember { mutableStateOf<List<PlayerSummaryResponse>>(emptyList()) }
    var selectedPlayerId by remember { mutableStateOf("") }
    var newPlayerName by remember { mutableStateOf("") }
    var playerFilter by remember { mutableStateOf("") }
    var effectiveDate by remember { mutableStateOf(kotlin.js.Date().let { d -> "${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}" }) }
    var notes by remember { mutableStateOf("") }
    var submitting by remember { mutableStateOf(false) }
    var successMessage by remember { mutableStateOf<String?>(null) }
    var successIsWarning by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var recentMoves by remember { mutableStateOf<List<RosterMoveResponse>>(emptyList()) }
    var loadingClubs by remember { mutableStateOf(true) }
    var rosterLoading by remember { mutableStateOf(false) }

    // Pick swap state
    var secondClubId by remember { mutableStateOf("") }
    var clubAPicks by remember { mutableStateOf<List<DraftPickResponse>>(emptyList()) }
    var clubBPicks by remember { mutableStateOf<List<DraftPickResponse>>(emptyList()) }
    var selectedClubAPickIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var selectedClubBPickIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var clubAPicksLoading by remember { mutableStateOf(false) }
    var clubBPicksLoading by remember { mutableStateOf(false) }
    var recentPickTrades by remember { mutableStateOf<List<DraftPickTradeResponse>>(emptyList()) }

    // Load clubs on mount
    LaunchedEffect(Unit) {
        try {
            val resp = apiGet("/api/clubs/list", ClubListResponse.serializer())
            clubs = resp.clubs
        } catch (_: Exception) {}
        loadingClubs = false
    }

    // Load recent moves and pick trades (refreshes when claims are resolved)
    LaunchedEffect(claimsRefreshKey) {
        try {
            val movesResp = apiGet("/api/rostermoves/list", RosterMoveListResponse.serializer())
            recentMoves = movesResp.moves
        } catch (_: Exception) {}
        try {
            val txnResp = apiGet("/api/transactions/list", TransactionListResponse.serializer())
            recentPickTrades = txnResp.transactions.filter { it.type == "PICK_TRADE" }.mapNotNull { it.pickTrade }
        } catch (_: Exception) {}
    }

    // Load roster when club changes
    LaunchedEffect(selectedClubId) {
        roster = emptyList()
        selectedSlot = ""
        if (selectedClubId.isNotEmpty()) {
            rosterLoading = true
            try {
                val leagueYear = apiGet("/api/leagueyears/detail", LeagueYearDetailResponse.serializer())
                val rosterResp = apiGet(
                    "/api/clubs/roster?clubId=$selectedClubId&leagueYearId=${leagueYear.id}",
                    ClubRosterResponse.serializer()
                )
                roster = rosterResp.players
            } catch (_: Exception) {}
            rosterLoading = false
        }
    }

    // Load players when slot changes (for add/drop existing player search)
    val currentSlotSport = roster.firstOrNull { it.rosterSlot == selectedSlot }?.sport
    LaunchedEffect(currentSlotSport) {
        allPlayers = emptyList()
        selectedPlayerId = ""
        playerFilter = ""
        if (currentSlotSport != null) {
            try {
                val resp = apiGet("/api/players/list?sport=$currentSlotSport", PlayerListResponse.serializer())
                allPlayers = resp.players
            } catch (_: Exception) {}
        }
    }

    // Load picks for club A when selected in pick swap mode
    LaunchedEffect(selectedClubId, moveType) {
        clubAPicks = emptyList()
        selectedClubAPickIds = emptySet()
        if (moveType == "PICK_SWAP" && selectedClubId.isNotEmpty()) {
            clubAPicksLoading = true
            try {
                val resp = apiGet("/api/draftpicktrades/club-picks?clubId=$selectedClubId", ClubPicksResponse.serializer())
                clubAPicks = resp.picks
            } catch (e: Exception) {
                console.error("Failed to load club A picks: ${e.message}")
            }
            clubAPicksLoading = false
        }
    }

    // Load picks for club B when selected in pick swap mode
    LaunchedEffect(secondClubId, moveType) {
        clubBPicks = emptyList()
        selectedClubBPickIds = emptySet()
        if (moveType == "PICK_SWAP" && secondClubId.isNotEmpty()) {
            clubBPicksLoading = true
            try {
                val resp = apiGet("/api/draftpicktrades/club-picks?clubId=$secondClubId", ClubPicksResponse.serializer())
                clubBPicks = resp.picks
            } catch (e: Exception) {
                console.error("Failed to load club B picks: ${e.message}")
            }
            clubBPicksLoading = false
        }
    }

    // Determine slots to show based on move type, sorted by position order
    val slotOrder = listOf(
        "GOLFER_1", "GOLFER_2", "GOLFER_3", "GOLFER_4", "GOLFER_5",
        "LIV_LINE",
        "MENS_TENNIS_1", "MENS_TENNIS_2", "MENS_TENNIS_3", "MENS_TENNIS_4",
        "WOMENS_TENNIS_1", "WOMENS_TENNIS_2",
    )
    val availableSlots = roster.filter { player ->
        if (moveType == "ROSTER_SWAP") {
            player.rosterSlot != "LIV_LINE" && player.sport == "MENS_GOLF"
        } else {
            true // ADD_DROP: all slots including LIV_LINE
        }
    }.sortedBy { slotOrder.indexOf(it.rosterSlot) }

    // Current player in selected slot
    val currentPlayer = roster.firstOrNull { it.rosterSlot == selectedSlot }

    // LIV Line player (for LIV Line swap)
    val livLinePlayer = roster.firstOrNull { it.rosterSlot == "LIV_LINE" }

    // Unrostered players for autocomplete
    val unrosteredPlayers = allPlayers.filter { p ->
        p.currentClub == null && (playerFilter.isEmpty() || p.name.lowercase().contains(playerFilter.lowercase()))
    }

    fun resetForm() {
        selectedSlot = ""
        playerMode = "existing"
        selectedPlayerId = ""
        newPlayerName = ""
        playerFilter = ""
        effectiveDate = kotlin.js.Date().let { d -> "${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}" }
        notes = ""
        secondClubId = ""
        selectedClubAPickIds = emptySet()
        selectedClubBPickIds = emptySet()
    }

    fun submit() {
        scope.launch {
            submitting = true
            errorMessage = null
            successMessage = null
            try {
                val request = ExecuteRosterMoveRequest(
                    moveType = moveType,
                    clubId = selectedClubId,
                    rosterSlot = selectedSlot,
                    addedPlayerId = if (moveType == "ADD_DROP" && playerMode == "existing") selectedPlayerId.ifEmpty { null } else null,
                    newPlayerName = if (moveType == "ADD_DROP" && playerMode == "new") newPlayerName.ifEmpty { null } else null,
                    effectiveDate = effectiveDate.ifEmpty { null },
                    notes = notes.ifEmpty { null },
                )
                val body = apiJson.encodeToString(request)
                val resp = apiPost("/api/rostermoves/execute", body, RosterMoveResponse.serializer())
                val action = if (moveType == "ADD_DROP") "Add/Drop" else "LIV Line Swap"
                successIsWarning = resp.warning != null
                successMessage = if (resp.warning != null) {
                    "$action completed. ${resp.warning}"
                } else {
                    "$action completed successfully"
                }
                recentMoves = listOf(resp) + recentMoves
                resetForm()

                // Refresh roster
                try {
                    val leagueYear = apiGet("/api/leagueyears/detail", LeagueYearDetailResponse.serializer())
                    val rosterResp = apiGet(
                        "/api/clubs/roster?clubId=$selectedClubId&leagueYearId=${leagueYear.id}",
                        ClubRosterResponse.serializer()
                    )
                    roster = rosterResp.players
                } catch (_: Exception) {}
            } catch (e: ApiException) {
                errorMessage = e.errorMessage
            } catch (e: Exception) {
                errorMessage = "Failed to execute roster move"
            }
            submitting = false
        }
    }

    fun submitPickSwap() {
        scope.launch {
            submitting = true
            errorMessage = null
            successMessage = null
            try {
                val request = ExecuteDraftPickTradeRequest(
                    clubAId = selectedClubId,
                    clubBId = secondClubId,
                    clubAPickIds = selectedClubAPickIds.toList(),
                    clubBPickIds = selectedClubBPickIds.toList(),
                    effectiveDate = effectiveDate.ifEmpty { null },
                    notes = notes.ifEmpty { null },
                )
                val body = apiJson.encodeToString(request)
                val resp = apiPost("/api/draftpicktrades/execute", body, DraftPickTradeResponse.serializer())
                successIsWarning = false
                successMessage = "Pick swap completed successfully"
                recentPickTrades = listOf(resp) + recentPickTrades
                resetForm()
            } catch (e: ApiException) {
                errorMessage = e.errorMessage
            } catch (e: Exception) {
                errorMessage = "Failed to execute pick swap"
            }
            submitting = false
        }
    }

    Column(Modifier.fillMaxWidth().gap(2.cssRem)) {
        // Title
        SpanText(
            "Manage Rosters",
            Modifier
                .fontFamily(*HeadingFontFamily)
                .fontSize(2.cssRem)
                .fontWeight(FontWeight.Bold)
                .color(SitePalette.text)
        )

        // Pending claims section
        if (pendingClaims.isNotEmpty()) {
            Column(Modifier.fillMaxWidth().gap(1.cssRem)) {
                SpanText(
                    "Pending Claims",
                    Modifier
                        .fontFamily(*HeadingFontFamily)
                        .fontSize(1.25.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .color(SitePalette.text)
                )
                pendingClaims.forEach { claim ->
                    PendingClaimCard(
                        claim = claim,
                        onRefresh = { claimsRefreshKey++ },
                    )
                }
            }
        }

        // Form card
        Column(
            Modifier
                .fillMaxWidth()
                .backgroundColor(SitePalette.surface)
                .border(1.px, LineStyle.Solid, SitePalette.border)
                .borderRadius(8.px)
                .padding(1.5.cssRem)
                .gap(1.25.cssRem)
        ) {
            // Move type toggle
            FormField("Move Type") {
                Row(Modifier.gap(1.cssRem)) {
                    RadioOption("ADD_DROP", "Add/Drop", moveType == "ADD_DROP") {
                        moveType = "ADD_DROP"; selectedSlot = ""
                    }
                    RadioOption("ROSTER_SWAP", "LIV Line Swap", moveType == "ROSTER_SWAP") {
                        moveType = "ROSTER_SWAP"; selectedSlot = ""
                    }
                    RadioOption("PICK_SWAP", "Pick Swap", moveType == "PICK_SWAP") {
                        moveType = "PICK_SWAP"; selectedSlot = ""; selectedClubId = ""; secondClubId = ""
                    }
                }
            }

            // Team selector
            FormField("Team") {
                if (loadingClubs) {
                    SpanText("Loading teams...", Modifier.color(SitePalette.textMuted).fontSize(0.875.cssRem))
                } else {
                    Select(attrs = {
                        addEventListener("change") { selectedClubId = it.target.asDynamic().value as? String ?: "" }
                        style(selectStyle())
                    }) {
                        Option("", attrs = { if (selectedClubId.isEmpty()) selected() }) { Text("Select a team...") }
                        clubs.forEach { club ->
                            Option(club.id, attrs = { if (selectedClubId == club.id) selected() }) { Text(club.name) }
                        }
                    }
                }
            }

            // Pick Swap UI
            if (moveType == "PICK_SWAP" && selectedClubId.isNotEmpty()) {
                // Club A picks
                if (clubAPicksLoading) {
                    SpanText("Loading picks...", Modifier.color(SitePalette.textMuted).fontSize(0.875.cssRem))
                } else if (clubAPicks.isNotEmpty()) {
                    FormField("${clubs.firstOrNull { it.id == selectedClubId }?.name ?: "Club A"} Picks") {
                        PickMultiSelect(
                            picks = clubAPicks,
                            selectedIds = selectedClubAPickIds,
                            onToggle = { id ->
                                selectedClubAPickIds = if (id in selectedClubAPickIds) selectedClubAPickIds - id else selectedClubAPickIds + id
                            },
                        )
                    }
                } else {
                    SpanText("No draft picks available", Modifier.color(SitePalette.textMuted).fontSize(0.875.cssRem))
                }

                // Second team selector
                FormField("Second Team") {
                    Select(attrs = {
                        addEventListener("change") { secondClubId = it.target.asDynamic().value as? String ?: "" }
                        style(selectStyle())
                    }) {
                        Option("", attrs = { if (secondClubId.isEmpty()) selected() }) { Text("Select a team...") }
                        clubs.filter { it.id != selectedClubId }.forEach { club ->
                            Option(club.id, attrs = { if (secondClubId == club.id) selected() }) { Text(club.name) }
                        }
                    }
                }

                // Club B picks
                if (secondClubId.isNotEmpty()) {
                    if (clubBPicksLoading) {
                        SpanText("Loading picks...", Modifier.color(SitePalette.textMuted).fontSize(0.875.cssRem))
                    } else if (clubBPicks.isNotEmpty()) {
                        FormField("${clubs.firstOrNull { it.id == secondClubId }?.name ?: "Club B"} Picks (optional)") {
                            PickMultiSelect(
                                picks = clubBPicks,
                                selectedIds = selectedClubBPickIds,
                                onToggle = { id ->
                                    selectedClubBPickIds = if (id in selectedClubBPickIds) selectedClubBPickIds - id else selectedClubBPickIds + id
                                },
                            )
                        }
                    }
                }
            }

            // Roster slot selector (for ADD_DROP and ROSTER_SWAP only)
            if (moveType != "PICK_SWAP" && selectedClubId.isNotEmpty()) {
                if (rosterLoading) {
                    SpanText("Loading roster...", Modifier.color(SitePalette.textMuted).fontSize(0.875.cssRem))
                } else {
                    FormField("Roster Slot") {
                        Select(attrs = {
                            addEventListener("change") { selectedSlot = it.target.asDynamic().value as? String ?: "" }
                            style(selectStyle())
                        }) {
                            Option("", attrs = { if (selectedSlot.isEmpty()) selected() }) { Text("Select a slot...") }
                            availableSlots.forEach { player ->
                                Option(player.rosterSlot, attrs = { if (selectedSlot == player.rosterSlot) selected() }) {
                                    Text("${formatSlotName(player.rosterSlot)} - ${player.name}")
                                }
                            }
                        }
                    }
                }
            }

            // Move-specific fields (ADD_DROP and ROSTER_SWAP)
            if (moveType != "PICK_SWAP" && selectedSlot.isNotEmpty()) {
                if (moveType == "ADD_DROP") {
                    // Dropping label
                    if (currentPlayer != null) {
                        FormField("Dropping") {
                            SpanText(
                                currentPlayer.name,
                                Modifier.fontWeight(FontWeight.SemiBold).color(SitePalette.text).fontSize(0.9375.cssRem)
                            )
                        }
                    }

                    // Adding: toggle between existing and new
                    FormField("Adding") {
                        Row(Modifier.gap(1.cssRem).margin(bottom = 0.5.cssRem)) {
                            RadioOption("existing", "Existing Player", playerMode == "existing") { playerMode = "existing" }
                            RadioOption("new", "New Player", playerMode == "new") { playerMode = "new" }
                        }

                        if (playerMode == "existing") {
                            Input(InputType.Text) {
                                placeholder("Search players...")
                                value(playerFilter)
                                onInput { playerFilter = it.value }
                                style {
                                    width(100.percent)
                                    padding(8.px, 12.px)
                                    fontSize(0.875.cssRem)
                                    border(1.px, LineStyle.Solid, SitePalette.border)
                                    borderRadius(6.px)
                                    backgroundColor(SitePalette.background)
                                    color(SitePalette.text)
                                    property("outline", "none")
                                }
                            }

                            if (playerFilter.isNotEmpty() && unrosteredPlayers.isNotEmpty()) {
                                Column(
                                    Modifier
                                        .fillMaxWidth()
                                        .maxHeight(12.cssRem)
                                        .overflow(Overflow.Auto)
                                        .border(1.px, LineStyle.Solid, SitePalette.border)
                                        .borderRadius(6.px)
                                        .backgroundColor(SitePalette.background)
                                ) {
                                    unrosteredPlayers.take(20).forEach { player ->
                                        Div(
                                            Modifier
                                                .fillMaxWidth()
                                                .padding(topBottom = 0.5.cssRem, leftRight = 0.75.cssRem)
                                                .cursor(Cursor.Pointer)
                                                .backgroundColor(if (selectedPlayerId == player.id) SitePalette.accent else SitePalette.background)
                                                .color(if (selectedPlayerId == player.id) SitePalette.background else SitePalette.text)
                                                .fontSize(0.875.cssRem)
                                                .onClick {
                                                    selectedPlayerId = player.id
                                                    playerFilter = player.name
                                                }
                                                .toAttrs()
                                        ) {
                                            Text(player.name)
                                        }
                                    }
                                }
                            }

                            if (playerFilter.isNotEmpty() && unrosteredPlayers.isEmpty() && allPlayers.isNotEmpty()) {
                                SpanText(
                                    "No unrostered players found matching \"$playerFilter\"",
                                    Modifier.color(SitePalette.textMuted).fontSize(0.8125.cssRem)
                                )
                            }
                        } else {
                            Input(InputType.Text) {
                                placeholder("Enter new player name...")
                                value(newPlayerName)
                                onInput { newPlayerName = it.value }
                                style {
                                    width(100.percent)
                                    padding(8.px, 12.px)
                                    fontSize(0.875.cssRem)
                                    border(1.px, LineStyle.Solid, SitePalette.border)
                                    borderRadius(6.px)
                                    backgroundColor(SitePalette.background)
                                    color(SitePalette.text)
                                    property("outline", "none")
                                }
                            }
                        }
                    }
                } else {
                    // LIV Line Swap info
                    if (currentPlayer != null && livLinePlayer != null) {
                        FormField("Swap Preview") {
                            Column(Modifier.gap(0.5.cssRem)) {
                                SpanText(
                                    "Promoting ${livLinePlayer.name} (LIV Line) to ${formatSlotName(selectedSlot)}",
                                    Modifier.fontSize(0.9375.cssRem).color(SitePalette.text)
                                )
                                SpanText(
                                    "Dropping ${currentPlayer.name}",
                                    Modifier.fontSize(0.9375.cssRem).color(SitePalette.textSecondary)
                                )
                            }
                        }
                    } else if (livLinePlayer == null) {
                        SpanText(
                            "No player on the LIV Line to promote",
                            Modifier.color(SitePalette.textMuted).fontSize(0.875.cssRem)
                        )
                    }
                }

                // Effective date
                FormField("Effective Date") {
                    Input(InputType.Date) {
                        value(effectiveDate)
                        onInput { effectiveDate = it.value }
                        style {
                            padding(8.px, 12.px)
                            fontSize(0.875.cssRem)
                            border(1.px, LineStyle.Solid, SitePalette.border)
                            borderRadius(6.px)
                            backgroundColor(SitePalette.background)
                            color(SitePalette.text)
                            property("outline", "none")
                        }
                    }
                }

                // Notes
                FormField("Notes (optional)") {
                    TextArea(value = notes, attrs = {
                        onInput { notes = it.value }
                        attr("placeholder", "Optional notes about this move...")
                        style {
                            width(100.percent)
                            padding(8.px, 12.px)
                            fontSize(0.875.cssRem)
                            border(1.px, LineStyle.Solid, SitePalette.border)
                            borderRadius(6.px)
                            backgroundColor(SitePalette.background)
                            color(SitePalette.text)
                            property("outline", "none")
                            property("resize", "vertical")
                            property("min-height", "3rem")
                        }
                    })
                }
            }

            // Pick swap: effective date + notes
            if (moveType == "PICK_SWAP" && selectedClubId.isNotEmpty() && secondClubId.isNotEmpty()) {
                FormField("Effective Date") {
                    Input(InputType.Date) {
                        value(effectiveDate)
                        onInput { effectiveDate = it.value }
                        style {
                            padding(8.px, 12.px)
                            fontSize(0.875.cssRem)
                            border(1.px, LineStyle.Solid, SitePalette.border)
                            borderRadius(6.px)
                            backgroundColor(SitePalette.background)
                            color(SitePalette.text)
                            property("outline", "none")
                        }
                    }
                }

                FormField("Notes (optional)") {
                    TextArea(value = notes, attrs = {
                        onInput { notes = it.value }
                        attr("placeholder", "Optional notes about this trade...")
                        style {
                            width(100.percent)
                            padding(8.px, 12.px)
                            fontSize(0.875.cssRem)
                            border(1.px, LineStyle.Solid, SitePalette.border)
                            borderRadius(6.px)
                            backgroundColor(SitePalette.background)
                            color(SitePalette.text)
                            property("outline", "none")
                            property("resize", "vertical")
                            property("min-height", "3rem")
                        }
                    })
                }
            }

            // Messages
            if (successMessage != null) {
                val bgColor = if (successIsWarning) Color("#fef9c3") else Color("#dcfce7")
                val borderColor = if (successIsWarning) Color("#fde68a") else Color("#86efac")
                val textColor = if (successIsWarning) Color("#854d0e") else Color("#166534")
                Div(
                    Modifier
                        .fillMaxWidth()
                        .backgroundColor(bgColor)
                        .border(1.px, LineStyle.Solid, borderColor)
                        .borderRadius(6.px)
                        .padding(0.75.cssRem)
                        .toAttrs()
                ) {
                    SpanText(successMessage!!, Modifier.color(textColor).fontSize(0.875.cssRem))
                }
            }

            if (errorMessage != null) {
                Div(
                    Modifier
                        .fillMaxWidth()
                        .backgroundColor(Color("#fef2f2"))
                        .border(1.px, LineStyle.Solid, Color("#fca5a5"))
                        .borderRadius(6.px)
                        .padding(0.75.cssRem)
                        .toAttrs()
                ) {
                    SpanText(errorMessage!!, Modifier.color(Color("#991b1b")).fontSize(0.875.cssRem))
                }
            }

            // Submit button
            val canSubmit = when {
                submitting -> false
                moveType == "PICK_SWAP" -> selectedClubId.isNotEmpty() && secondClubId.isNotEmpty() && selectedClubAPickIds.isNotEmpty()
                selectedSlot.isEmpty() -> false
                moveType == "ADD_DROP" && playerMode == "existing" -> selectedPlayerId.isNotEmpty()
                moveType == "ADD_DROP" && playerMode == "new" -> newPlayerName.isNotBlank()
                moveType == "ROSTER_SWAP" -> livLinePlayer != null
                else -> false
            }

            if (canSubmit || (moveType == "PICK_SWAP" && selectedClubAPickIds.isNotEmpty()) || selectedSlot.isNotEmpty()) {
                if (moveType == "PICK_SWAP") {
                    // Pick swap confirmation with trade summary
                    val clubAName = clubs.firstOrNull { it.id == selectedClubId }?.name ?: "Club A"
                    val clubBName = clubs.firstOrNull { it.id == secondClubId }?.name ?: "Club B"
                    val clubAPickLabels = clubAPicks
                        .filter { it.id in selectedClubAPickIds }
                        .joinToString(", ") { formatPickLabel(it) }
                    val clubBPickLabels = clubBPicks
                        .filter { it.id in selectedClubBPickIds }
                        .joinToString(", ") { formatPickLabel(it) }

                    // Trade summary preview
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .backgroundColor(SitePalette.background)
                            .border(1.px, LineStyle.Solid, SitePalette.border)
                            .borderRadius(8.px)
                            .padding(1.cssRem)
                            .gap(0.375.cssRem)
                    ) {
                        SpanText(
                            "Trade Summary",
                            Modifier.fontSize(0.8125.cssRem).fontWeight(FontWeight.SemiBold)
                                .color(SitePalette.textSecondary).textTransform(TextTransform.Uppercase)
                        )
                        SpanText(
                            "$clubAName sends: $clubAPickLabels",
                            Modifier.fontSize(0.875.cssRem).color(SitePalette.text)
                        )
                        if (clubBPickLabels.isNotEmpty()) {
                            SpanText(
                                "$clubBName sends: $clubBPickLabels",
                                Modifier.fontSize(0.875.cssRem).color(SitePalette.text)
                            )
                        }
                    }

                    Row(Modifier.fillMaxWidth().position(Position.Relative)) {
                        ConfirmableButton(
                            label = "Execute Trade",
                            loadingLabel = "Submitting...",
                            confirmLabel = "Are you sure?",
                            isLoading = submitting,
                            enabled = canSubmit,
                            buttonModifier = Modifier
                                .backgroundColor(if (canSubmit) SitePalette.primary else SitePalette.border)
                                .color(if (canSubmit) Colors.White else SitePalette.textMuted)
                                .borderRadius(8.px)
                                .padding(topBottom = 0.625.cssRem, leftRight = 1.25.cssRem)
                                .fontSize(0.9375.cssRem)
                                .fontWeight(FontWeight.SemiBold)
                                .transition(Transition.of("background-color", 150.ms)),
                            onConfirm = { submitPickSwap() },
                        )
                    }
                } else {
                    Row(Modifier.fillMaxWidth().position(Position.Relative)) {
                        ConfirmableButton(
                            label = "Execute Move",
                            loadingLabel = "Submitting...",
                            confirmLabel = "Are you sure?",
                            isLoading = submitting,
                            enabled = canSubmit,
                            buttonModifier = Modifier
                                .backgroundColor(if (canSubmit) SitePalette.primary else SitePalette.border)
                                .color(if (canSubmit) Colors.White else SitePalette.textMuted)
                                .borderRadius(8.px)
                                .padding(topBottom = 0.625.cssRem, leftRight = 1.25.cssRem)
                                .fontSize(0.9375.cssRem)
                                .fontWeight(FontWeight.SemiBold)
                                .transition(Transition.of("background-color", 150.ms)),
                            onConfirm = { submit() },
                        )
                    }
                }
            }
        }

        // Recent moves section
        if (recentMoves.isNotEmpty() || recentPickTrades.isNotEmpty()) {
            Column(Modifier.fillMaxWidth().gap(1.cssRem)) {
                SpanText(
                    "Recent Moves",
                    Modifier
                        .fontFamily(*HeadingFontFamily)
                        .fontSize(1.25.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .color(SitePalette.text)
                )

                recentPickTrades.forEach { trade ->
                    PickTradeCard(trade)
                }
                recentMoves.forEach { move ->
                    MoveCard(move)
                }
            }
        }
    }
}

@Composable
private fun FormField(label: String, content: @Composable () -> Unit) {
    Column(Modifier.fillMaxWidth().gap(0.375.cssRem)) {
        SpanText(
            label,
            Modifier
                .fontSize(0.8125.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .color(SitePalette.textSecondary)
                .textTransform(TextTransform.Uppercase)
        )
        content()
    }
}

@Composable
private fun RadioOption(value: String, label: String, checked: Boolean, onClick: () -> Unit) {
    Row(
        Modifier
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .gap(0.375.cssRem),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Input(InputType.Radio) {
            this.checked(checked)
            onChange { onClick() }
        }
        SpanText(label, Modifier.fontSize(0.9375.cssRem).color(SitePalette.text))
    }
}

private fun selectStyle(): StyleScope.() -> Unit = {
    width(100.percent)
    padding(8.px, 12.px)
    fontSize(0.875.cssRem)
    border(1.px, LineStyle.Solid, SitePalette.border)
    borderRadius(6.px)
    backgroundColor(SitePalette.background)
    color(SitePalette.text)
    property("outline", "none")
}

private fun formatPickLabel(pick: DraftPickResponse): String {
    val roundLabel = if (pick.round == "FIRST") "1st Round" else "2nd Round"
    return if (pick.isTraded) "${pick.year} - $roundLabel - via ${pick.originalOwnerName}"
    else "${pick.year} - $roundLabel"
}

@Composable
private fun PickMultiSelect(
    picks: List<DraftPickResponse>,
    selectedIds: Set<String>,
    onToggle: (String) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderRadius(6.px)
            .backgroundColor(SitePalette.background)
            .overflow(Overflow.Hidden)
    ) {
        picks.forEach { pick ->
            val isSelected = pick.id in selectedIds
            Row(
                Modifier
                    .fillMaxWidth()
                    .padding(topBottom = 0.625.cssRem, leftRight = 0.75.cssRem)
                    .cursor(Cursor.Pointer)
                    .backgroundColor(if (isSelected) SitePalette.accent else SitePalette.background)
                    .color(if (isSelected) SitePalette.background else SitePalette.text)
                    .onClick { onToggle(pick.id) }
                    .gap(0.5.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    if (isSelected) "✓" else " ",
                    Modifier.fontSize(0.875.cssRem).fontWeight(FontWeight.Bold)
                )
                SpanText(
                    formatPickLabel(pick),
                    Modifier.fontSize(0.875.cssRem)
                )
            }
        }
    }
}

private fun formatSlotName(slot: String): String = when (slot) {
    "GOLFER_1" -> "Golfer 1"
    "GOLFER_2" -> "Golfer 2"
    "GOLFER_3" -> "Golfer 3"
    "GOLFER_4" -> "Golfer 4"
    "GOLFER_5" -> "Golfer 5"
    "LIV_LINE" -> "LIV Line"
    "MENS_TENNIS_1" -> "Men's Tennis 1"
    "MENS_TENNIS_2" -> "Men's Tennis 2"
    "MENS_TENNIS_3" -> "Men's Tennis 3"
    "MENS_TENNIS_4" -> "Men's Tennis 4"
    "WOMENS_TENNIS_1" -> "Women's Tennis 1"
    "WOMENS_TENNIS_2" -> "Women's Tennis 2"
    else -> slot
}
